import { useCallback, useRef, type MouseEvent, type ReactNode } from 'react';
import { Skeleton } from '@/components/States';
import { DEFAULT_COLUMN_WIDTH, type UseReportLayout } from '@/features/reports/useReportLayout';

/**
 * Horizontal scroll container for wide report tables. The scroller — not the
 * page — is what moves, and it is focusable with an accessible name so keyboard
 * users can scroll it with the arrow keys.
 */
export function TableScroll({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      // `relative` matters: the sr-only caption/headers inside are absolutely
      // positioned, and without a positioned ancestor they escape this
      // scroller and make the PAGE scroll sideways instead of the table.
      className="relative overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600"
    >
      {children}
    </div>
  );
}

export type ColumnAlign = 'left' | 'right';

export interface Column<T> {
  /** Stable identity, also used to address the totals row. */
  key: string;
  header: string;
  align?: ColumnAlign;
  /** Extra classes for the cell (not the header). */
  cellClassName?: string;
  /** Tailwind responsive-visibility classes, e.g. `hidden sm:table-cell`. */
  visibility?: string;
  /** Keeps the header for assistive tech but hides it visually (action columns). */
  headerHidden?: boolean;
  /**
   * Pinned columns: always shown, always first, and never offered in the
   * Display-options picker (they carry the row's identity). Still
   * resizable.
   */
  pinned?: boolean;
  cell: (row: T) => ReactNode;
}

/**
 * Apply a page's saved layout to its column list: pinned columns first,
 * then the user's order, with hidden ones dropped.
 *
 * Kept a plain function rather than folded into `DataTable` so a page can
 * feed the SAME resolved list to its CSV/print path and be sure the export
 * matches what's on screen.
 */
export function applyReportLayout<T>(
  columns: ReadonlyArray<Column<T>>,
  layout: UseReportLayout | undefined,
): Column<T>[] {
  if (!layout) return [...columns];
  const byKey = new Map(columns.map((column) => [column.key, column]));
  const pinned = columns.filter((column) => column.pinned === true);
  const rest = layout.orderedKeys
    .map((key) => byKey.get(key))
    .filter((column): column is Column<T> => column !== undefined && column.pinned !== true)
    .filter((column) => layout.isVisible(column.key));
  return [...pinned, ...rest];
}

export interface TotalsRow {
  label: string;
  /** Keyed by column key; columns without an entry render blank. */
  cells: Record<string, ReactNode>;
}

export interface DataTableProps<T> {
  /** Screen-reader caption describing the table. */
  caption: string;
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T, index: number) => string;
  totals?: TotalsRow;
  /** Extra classes per row, e.g. to tint a short shift. */
  rowClassName?: (row: T) => string | undefined;
  /**
   * Makes the whole row clickable as a MOUSE CONVENIENCE.
   *
   * It is never the only way in: a table using this must still put a real link
   * or button in one of its cells, because a clickable `<tr>` cannot be tabbed
   * to, announced, opened in a new tab, or reached by a screen reader. This
   * widens the hit area for pointer users; the link carries everyone else.
   */
  onRowClick?: (row: T) => void;
  /**
   * When supplied, column widths come from the user's saved layout and each
   * header grows a drag handle on its right edge.
   */
  layout?: UseReportLayout;
}

/**
 * The draggable divider on a column's right edge.
 *
 * Pointer-events based (not mouse) so a trackpad/touch drag works too, and
 * it captures the pointer, so a fast drag that leaves the 8px strip keeps
 * resizing instead of stopping dead. `onClick` is stopped as well: the
 * handle sits inside a `<th>` that may also sort.
 */
function ColumnResizeHandle({ columnKey, layout }: { columnKey: string; layout: UseReportLayout }) {
  const lastX = useRef<number | null>(null);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    lastX.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      if (lastX.current === null) return;
      const delta = event.clientX - lastX.current;
      if (delta === 0) return;
      lastX.current = event.clientX;
      layout.resize(columnKey, delta);
    },
    [columnKey, layout],
  );

  const endDrag = useCallback((event: React.PointerEvent<HTMLSpanElement>) => {
    lastX.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${columnKey} column`}
      className="absolute right-0 top-0 flex h-full w-2 translate-x-1/2 cursor-col-resize touch-none items-center justify-center"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(event) => event.stopPropagation()}
    >
      <span className="h-1/2 w-px bg-slate-300" />
    </span>
  );
}

/** Right-aligned columns are numeric, and figures must never wrap mid-amount. */
function alignClass(align: ColumnAlign | undefined): string {
  return align === 'right' ? 'text-right whitespace-nowrap' : '';
}

/** Report table: one place for the header/row/totals styling and semantics. */
export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  totals,
  rowClassName,
  onRowClick,
  layout,
}: DataTableProps<T>) {
  /**
   * Three things must NOT trigger a row navigation, or the row becomes hostile:
   *
   *  1. a click that landed on a link or button inside the row — that control
   *     already handles it, and firing both double-navigates
   *  2. a modified click (cmd/ctrl/shift/middle) — those mean "new tab" or
   *     "extend selection", and the inner link handles them natively
   *  3. a click that ended a text selection — dragging to copy an invoice number
   *     must not navigate away from what you just selected
   */
  const handleRowClick = (row: T) => (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('a, button, input, select, label')) return;
    if ((window.getSelection()?.toString() ?? '') !== '') return;
    onRowClick?.(row);
  };
  return (
    <TableScroll label={caption}>
      <table className="min-w-full divide-y divide-slate-200">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-surface">
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.key}
                scope="col"
                style={
                  layout
                    ? { width: layout.widthFor(column.key) ?? DEFAULT_COLUMN_WIDTH }
                    : undefined
                }
                className={`th ${layout ? 'relative' : ''} ${alignClass(column.align)} ${
                  column.visibility ?? ''
                }`.trim()}
              >
                {column.headerHidden === true ? <span className="sr-only">{column.header}</span> : column.header}
                {/* No handle on the last column — nothing to resize into. */}
                {layout && index < columns.length - 1 ? (
                  <ColumnResizeHandle columnKey={column.key} layout={layout} />
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((row, index) => (
            <tr
              key={rowKey(row, index)}
              onClick={onRowClick ? handleRowClick(row) : undefined}
              className={`${rowClassName?.(row) ?? ''} ${
                onRowClick ? 'cursor-pointer transition-colors hover:bg-brand-600/[0.04]' : ''
              }`.trim()}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`td ${alignClass(column.align)} ${column.visibility ?? ''} ${
                    column.cellClassName ?? ''
                  }`.trim()}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {totals ? (
          <tfoot className="border-t border-slate-200 bg-surface">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  scope={index === 0 ? 'row' : undefined}
                  className={`td font-semibold text-slate-900 ${alignClass(column.align)} ${
                    column.visibility ?? ''
                  }`.trim()}
                >
                  {index === 0 ? totals.label : (totals.cells[column.key] ?? null)}
                </th>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </TableScroll>
  );
}

/** Row-shaped placeholder used while a table loads. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-5 py-4">
          <Skeleton className="h-3.5 w-40" />
          {Array.from({ length: Math.max(0, columns - 1) }).map((__, cellIndex) => (
            <Skeleton key={cellIndex} className="hidden h-3.5 w-16 sm:block" />
          ))}
        </div>
      ))}
    </div>
  );
}
